/**
 * Entity kanban page (route `/entity-kanban`).
 *
 * Spec §10.2: "All parts and sub-parts grouped by current stage."
 *
 * Layout: 18 stage columns side-by-side by default, with URL-backed filters
 * for search, project, entity type, current stage, visible columns, and age in
 * stage. The column container scrolls horizontally so the user can pan through
 * stages.
 *
 * Card content: item name, project ref, days-in-stage, parent-part badge
 * for SUB_PARTs (gives at-a-glance variant context).
 *
 * Card click → entity drilldown.
 */
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Box, FileText, Package2, Search, X } from 'lucide-react';
import { stageLabel } from '@procurement/shared';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/form-field';
import { PageHeader } from '@/components/ui/page-header';
import { ProjectNumber } from '@/components/ui/project-number';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Spinner } from '@/components/ui/spinner';
import {
  ENTITY_KANBAN_STAGE_NUMBERS,
  applyEntityKanbanFilters,
  buildKanbanColumns,
  getEntityKanbanProjectOptions,
  parseVisibleStagesParam,
  serializeVisibleStagesParam,
  type EntityKanbanColumn,
} from '@/lib/entity-kanban';
import { useEntityKanban, type EntityKanbanItem } from '@/lib/visibility-queries';
import { cn } from '@/lib/cn';
import { usePageTitle } from '@/lib/use-page-title';

interface EntityKanbanSearch {
  q?: string;
  projectId?: string;
  kind?: 'PART' | 'SUB_PART';
  stage?: number;
  visibleStages?: string;
  minDays?: number;
}

const KIND_ITEMS = [
  { value: 'PART', label: 'Parts only' },
  { value: 'SUB_PART', label: 'Sub-parts only' },
];

const MIN_DAYS_ITEMS = [
  { value: '3', label: '3+ days' },
  { value: '7', label: '7+ days' },
  { value: '14', label: '14+ days' },
  { value: '30', label: '30+ days' },
  { value: '60', label: '60+ days' },
];

const STAGE_ITEMS = ENTITY_KANBAN_STAGE_NUMBERS.map((stageNumber) => ({
  value: String(stageNumber),
  label: `${stageNumber} — ${stageLabel(stageNumber)}`,
}));

export function EntityKanbanPage() {
  usePageTitle('Entity board');
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as EntityKanbanSearch;
  const [searchInput, setSearchInput] = useState(search.q ?? '');
  const query = useEntityKanban();

  const rawItems = query.data?.items ?? [];
  const projectOptions = useMemo(() => getEntityKanbanProjectOptions(rawItems), [rawItems]);
  const visibleStages = useMemo(
    () => parseVisibleStagesParam(search.visibleStages),
    [search.visibleStages],
  );

  const filteredItems = useMemo(
    () =>
      applyEntityKanbanFilters(rawItems, {
        q: search.q,
        projectId: search.projectId,
        kind: search.kind,
        stage: search.stage,
        minDays: search.minDays,
      }),
    [rawItems, search.kind, search.minDays, search.projectId, search.q, search.stage],
  );

  const columns = useMemo(
    () => buildKanbanColumns(filteredItems, { visibleStages }),
    [filteredItems, visibleStages],
  );

  const totalCount = rawItems.length;
  const filteredCount = filteredItems.length;
  const activeFilters = Boolean(
    search.q ||
      search.projectId ||
      search.kind ||
      search.stage !== undefined ||
      search.visibleStages !== undefined ||
      search.minDays !== undefined,
  );
  const stageHiddenByColumns =
    search.stage !== undefined &&
    visibleStages !== undefined &&
    !visibleStages.includes(search.stage);

  function updateSearch(next: Partial<EntityKanbanSearch>) {
    void navigate({
      to: '/entity-kanban',
      search: (prev: EntityKanbanSearch) => {
        const merged: EntityKanbanSearch = { ...prev, ...next };
        if (!merged.q) delete merged.q;
        if (!merged.projectId) delete merged.projectId;
        if (!merged.kind) delete merged.kind;
        if (merged.stage === undefined) delete merged.stage;
        if (!merged.visibleStages) delete merged.visibleStages;
        if (merged.minDays === undefined) delete merged.minDays;
        return merged;
      },
      replace: true,
    });
  }

  function clearFilters() {
    setSearchInput('');
    void navigate({ to: '/entity-kanban', search: {} as EntityKanbanSearch, replace: true });
  }

  function submitSearch(e: FormEvent) {
    e.preventDefault();
    updateSearch({ q: searchInput.trim() || undefined });
  }

  return (
    <>
      <PageHeader
        title="Entity kanban"
        subtitle="Every live part and sub-part across open projects, grouped by current pipeline stage."
      />

      <Card className="mb-4 space-y-3">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
          <form onSubmit={submitSearch} className="min-w-0 flex-1">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-tertiary"
                strokeWidth={1.5}
              />
              <Input
                placeholder="Search entities, project no., title, stage…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="h-9 pl-7 text-sm"
              />
            </div>
          </form>
          {activeFilters ? (
            <Button
              type="button"
              size="compact"
              variant="secondary"
              leftIcon={<X className="h-3.5 w-3.5" strokeWidth={1.5} />}
              onClick={clearFilters}
            >
              Clear filters
            </Button>
          ) : null}
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <FilterBlock label="Project">
            <SearchableSelect
              items={projectOptions}
              value={search.projectId ?? null}
              onChange={(value) => updateSearch({ projectId: value ?? undefined })}
              placeholder="All projects"
              emptyMessage="No projects"
              clearable
            />
          </FilterBlock>

          <FilterBlock label="Entity type">
            <SearchableSelect
              items={KIND_ITEMS}
              value={search.kind ?? null}
              onChange={(value) =>
                updateSearch({ kind: value === 'PART' || value === 'SUB_PART' ? value : undefined })
              }
              placeholder="All entities"
              clearable
            />
          </FilterBlock>

          <FilterBlock label="Stage filter">
            <SearchableSelect
              items={STAGE_ITEMS}
              value={search.stage === undefined ? null : String(search.stage)}
              onChange={(value) => {
                if (value === null) {
                  updateSearch({ stage: undefined });
                  return;
                }
                const stage = Number(value);
                updateSearch({
                  stage: Number.isInteger(stage) && stage >= 1 && stage <= 18 ? stage : undefined,
                });
              }}
              placeholder="All stages"
              clearable
            />
          </FilterBlock>

          <FilterBlock label="Visible columns" className="md:col-span-2 xl:col-span-1">
            <SearchableMultiSelect
              items={STAGE_ITEMS}
              values={visibleStages === undefined ? null : visibleStages.map(String)}
              onChange={(values) => {
                const stages = values === null ? null : values.map(Number);
                updateSearch({ visibleStages: serializeVisibleStagesParam(stages) });
              }}
              placeholder="All columns"
              noneLabel="No columns selected"
              emptyMessage="No stages"
            />
          </FilterBlock>

          <FilterBlock label="Min days in stage">
            <SearchableSelect
              items={MIN_DAYS_ITEMS}
              value={search.minDays === undefined ? null : String(search.minDays)}
              onChange={(value) => {
                if (value === null) {
                  updateSearch({ minDays: undefined });
                  return;
                }
                const minDays = Number(value);
                updateSearch({
                  minDays: Number.isInteger(minDays) && minDays > 0 ? minDays : undefined,
                });
              }}
              placeholder="Any age"
              clearable
            />
          </FilterBlock>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-secondary">
          <span>
            Showing {filteredCount} of {totalCount} entit{totalCount === 1 ? 'y' : 'ies'}
          </span>
          <span>·</span>
          <span>
            {visibleStages === undefined
              ? 'All 18 columns shown'
              : `${columns.length} column${columns.length === 1 ? '' : 's'} shown`}
          </span>
          {stageHiddenByColumns ? (
            <span className="text-warning-strong">
              · Your stage filter is hidden by the visible-column selection.
            </span>
          ) : null}
        </div>
      </Card>

      {query.isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner className="h-6 w-6 text-secondary" />
        </div>
      ) : query.error ? (
        <Card>
          <p className="text-sm text-danger-fg">Failed to load. {query.error.message}</p>
        </Card>
      ) : totalCount === 0 ? (
        <EmptyState
          icon={<FileText className="h-6 w-6" strokeWidth={1.5} />}
          title="Nothing in flight"
          body="No live parts or sub-parts have advanced past stage 0 yet."
        />
      ) : filteredCount === 0 ? (
        <Card>
          <EmptyState
            icon={<Search className="h-6 w-6" strokeWidth={1.5} />}
            title="No entities match your filters"
            body="Try clearing the search, project, stage, or age filters."
          />
        </Card>
      ) : columns.length === 0 ? (
        <Card>
          <EmptyState
            icon={<FileText className="h-6 w-6" strokeWidth={1.5} />}
            title="No columns selected"
            body="Choose at least one stage in Visible columns, or select all columns."
          />
        </Card>
      ) : (
        <div className="-mx-2 overflow-x-auto px-2 pb-2">
          <div className="flex gap-3" style={{ minWidth: 'fit-content' }}>
            {columns.map((column) => (
              <KanbanColumn key={column.stageNumber} column={column} />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function FilterBlock({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className}>
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-tertiary">
        {label}
      </div>
      {children}
    </div>
  );
}

function KanbanColumn({ column }: { column: EntityKanbanColumn }) {
  const isBatchStage = column.stageNumber <= 5;
  return (
    <div className="flex w-[240px] shrink-0 flex-col rounded-md border border-border-default bg-surface">
      <div
        className={cn(
          'flex items-center justify-between border-b border-border-default px-3 py-2 text-xs',
          isBatchStage ? 'bg-subtle' : 'bg-surface',
        )}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[10px] text-secondary">{column.stageNumber}</span>
            <span className="truncate font-semibold text-primary">{column.stageLabel}</span>
          </div>
        </div>
        <span className="shrink-0 rounded-full bg-subtle px-1.5 py-0.5 text-[10px] font-medium text-secondary">
          {column.items.length}
        </span>
      </div>
      <div className="flex-1 space-y-2 p-2">
        {column.items.length === 0 ? (
          <p className="px-1 py-2 text-[11px] italic text-tertiary">No items</p>
        ) : (
          column.items.map((item) => (
            <EntityCard key={`${item.entityKind}-${item.entityId}`} item={item} />
          ))
        )}
      </div>
    </div>
  );
}

function EntityCard({ item }: { item: EntityKanbanItem }) {
  const Icon = item.entityKind === 'SUB_PART' ? Box : Package2;
  return (
    <Link
      to="/projects/$projectId/items/$itemId"
      params={{ projectId: item.projectId, itemId: item.entityId }}
      className="block rounded-md border border-border-default bg-base p-2 transition-colors hover:bg-subtle"
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5 text-tertiary">
          <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-medium text-primary">{item.itemName}</div>
          {item.parentItemName ? (
            <div className="truncate text-[11px] text-secondary">↳ {item.parentItemName}</div>
          ) : null}
          {/* E19: project title shown under the part so the card carries its
           * project context without forcing the user to map project numbers
           * to titles in their head. */}
          <div className="truncate text-[11px] text-secondary">{item.projectTitle}</div>
          <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-secondary">
            <ProjectNumber number={item.projectNumber} size="sm" />
            <span className="shrink-0">{item.daysInCurrentStage}d</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
